import { RequiredTooltipProvider } from '@/shared/components/required-field-tooltip';
import { isRegisterNumberCnpjMask } from '@/shared/utils/format-register-number';
import { Spinner, Steps, Text } from '@noorden-insurtech/alba-design-system';
import { useEffect, useMemo, useState } from 'react';
import { useProposalForm } from '../hooks/use-proposal-form.hook';
import { useProposalSchema } from '../hooks/use-proposal-schema.hook';
import { useProductPaymentConfig } from '@/features/policies/hooks';
import { useProposal } from '../hooks/use-proposal.hook';
import {
  getPaymentProductType,
  getProposalCategory,
} from '../utils/proposal-category';
import { StepInsureData } from './proposal-steps/step-insure-data.component';
import { StepPayment } from './proposal-steps/step-payment.component';
import { StepResume } from './proposal-steps/step-resume.component';

type Props = {
  mode: 'edit' | 'view';
  policyId?: string;
};

export function NewProposalForm({ mode, policyId }: Props) {
  const isViewMode = mode === 'view';

  const { data: proposal, isLoading: isLoadingProposal } =
    useProposal(policyId);
  const proposalCategory = useMemo(
    () => getProposalCategory(proposal?.productCode),
    [proposal?.productCode]
  );
  const isBusinessProposal = proposalCategory === 'BUSINESS_INSURANCE';
  /**
   * Cada produto tem o SEU catálogo de formas de pagamento na DataTable
   * `PaymentMethod`. Resolvido aqui e passado por prop: os steps não precisam
   * saber que produto é esse, e ninguém herda o combo do residencial por
   * esquecer de informar.
   */
  const paymentProductType = useMemo(
    () => getPaymentProductType(proposalCategory),
    [proposalCategory]
  );
  const { data: schemaCategory } = useProposalSchema(proposalCategory, {
    enabled: Boolean(proposal?.productCode),
  });

  /**
   * Config de cobrança do produto. Quando ele cobra pela Finance.API, a proposta
   * aceita UMA única forma de pagamento — o link passa a ser o canal de cobrança,
   * então "meio diferente nas demais parcelas" deixa de fazer sentido.
   *
   * A restrição chega ao step como PROP, resolvida aqui a partir de dados: o
   * componente de pagamento continua sem saber que produto é esse.
   */
  const { data: paymentConfig } = useProductPaymentConfig(
    proposal?.productCode
  );
  const allowDifferentPayment = paymentConfig?.singlePaymentMethodOnly !== true;
  /**
   * Quando o produto cobra pelo gateway, o combo mostra as formas do gateway
   * ligadas e as legadas do catálogo; só as `gateway…` são cobradas por link.
   * Vazio = sem filtro, como em todo produto do fluxo tradicional.
   */
  const allowedPaymentMethodCodes =
    paymentConfig?.allowedPaymentMethodCodes ?? [];
  const gatewayPaymentMethodCodes =
    paymentConfig?.gatewayPaymentMethodCodes ?? [];
  /** Formas do gateway que ninguém cobraria agora — saem do combo, com ou sem gateway. */
  const hiddenPaymentMethodCodes =
    paymentConfig?.hiddenPaymentMethodCodes ?? [];
  const chargeLeadDays =
    paymentConfig?.paymentProvider === 'FINANCE_API'
      ? paymentConfig.chargeLeadDays
      : undefined;
  const pixAutomatic =
    paymentConfig?.paymentProvider === 'FINANCE_API' &&
    paymentConfig.pixBillingMode === 'AUTOMATIC';
  const cardSingleTransaction =
    paymentConfig?.paymentProvider === 'FINANCE_API' &&
    paymentConfig.cardBillingMode === 'SINGLE_TRANSACTION';
  const [isPrefilled, setIsPrefilled] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const [hasSetInitialStep, setHasSetInitialStep] = useState(false);

  useEffect(() => {
    if (isViewMode && schemaCategory?.steps.length && !hasSetInitialStep) {
      setCurrentStep(schemaCategory.steps.length - 1);
      setHasSetInitialStep(true);
    }
  }, [isViewMode, schemaCategory?.steps.length, hasSetInitialStep]);

  const mainForm = useProposalForm({
    defaultValues: {
      pk: 0,
      name: '',
      hasSocialName: false,
      socialName: '',
      registerNumber: '',
      gender: '',
      birthDate: '',
      phone: '',
      email: '',
      startDate: '',
      endDate: '',
      billingCep: '',
      billingStreet: '',
      billingNumber: '',
      billingComplement: '',
      billingNeighborhood: '',
      billingCity: '',
      billingState: '',
      originalAddress: {
        cep: '',
        street: '',
        addressNumber: '',
        addressComplement: '',
        neighborhood: '',
        city: '',
        state: '',
      },
      useInsuredAddressAsBillingAddress: false,
      selectedRiskPk: '',
    },
    onSubmit: async () => {
      // TODO: Implementar submissão do formulário
    },
  });

  useEffect(() => {
    setIsPrefilled(false);
  }, [policyId]);

  useEffect(() => {
    if (!proposal || isPrefilled) return;

    const customer = proposal.customer;
    const registerNumber =
      customer?.cpf ||
      (customer as { documentNumber?: string })?.documentNumber ||
      '';
    const isLegalPerson =
      customer?.documentType === '2' ||
      customer?.isOrgParty === 'Y' ||
      isRegisterNumberCnpjMask(registerNumber);

    const toDateOnly = (value?: string) =>
      value ? value.split('T')[0] || value : '';

    const socialNameTrimmed = (customer?.socialName ?? '').trim();
    const hasSocialNameFromApi = !isLegalPerson && socialNameTrimmed.length > 0;

    mainForm.setFieldValue('pk', customer?.pk || 0);
    mainForm.setFieldValue('name', customer?.name || '');
    mainForm.setFieldValue('registerNumber', registerNumber);
    mainForm.setFieldValue('hasSocialName', hasSocialNameFromApi);
    mainForm.setFieldValue(
      'socialName',
      hasSocialNameFromApi ? socialNameTrimmed : ''
    );
    mainForm.setFieldValue(
      'gender',
      isLegalPerson ? '' : String(customer?.genderCode || '').trim()
    );
    mainForm.setFieldValue(
      'birthDate',
      isLegalPerson ? '' : toDateOnly(customer?.dateOfBirth)
    );
    mainForm.setFieldValue(
      'phone',
      customer?.cellphone || customer?.phone || ''
    );
    mainForm.setFieldValue('email', customer?.email || '');

    mainForm.setFieldValue('startDate', toDateOnly(proposal?.effectiveDate));
    mainForm.setFieldValue(
      'endDate',
      toDateOnly(proposal?.expiryDate || proposal?.terminateDate)
    );

    const risk = proposal?.lobs?.[0]?.risks?.[0];
    const billingAddress = (
      proposal as {
        billingAddress?: {
          zipCode?: string;
          cep?: string;
          address?: string;
          street?: string;
          addressNumber?: string;
          number?: string;
          addressComplement?: string;
          complement?: string;
          neighborhood?: string;
          city?: string;
          state?: string;
        };
      }
    )?.billingAddress;

    const resolvedBilling = {
      cep: billingAddress?.zipCode || billingAddress?.cep,
      street: billingAddress?.address || billingAddress?.street,
      addressNumber: billingAddress?.addressNumber || billingAddress?.number,
      addressComplement:
        billingAddress?.addressComplement || billingAddress?.complement,
      neighborhood: billingAddress?.neighborhood,
      city: billingAddress?.city,
      state: billingAddress?.state,
    };

    const cameFromInsuredRiskAddress =
      resolvedBilling.cep === risk?.zipCode &&
      resolvedBilling.street === risk?.address &&
      resolvedBilling.city === risk?.city &&
      resolvedBilling.state === risk?.state;

    mainForm.setFieldValue(
      'useInsuredAddressAsBillingAddress',
      cameFromInsuredRiskAddress
    );

    mainForm.setFieldValue('originalAddress', {
      cep: resolvedBilling.cep || risk?.zipCode || '',
      street: resolvedBilling.street || risk?.address || '',
      addressNumber: resolvedBilling.addressNumber || risk?.addressNumber || '',
      addressComplement:
        resolvedBilling.addressComplement || risk?.addressComplement || '',
      neighborhood: resolvedBilling.neighborhood || risk?.neighborhood || '',
      city: resolvedBilling.city || risk?.city || '',
      state: resolvedBilling.state || risk?.state || '',
    });

    mainForm.setFieldValue('billingCep', resolvedBilling.cep || '');
    mainForm.setFieldValue('billingStreet', resolvedBilling.street || '');
    mainForm.setFieldValue(
      'billingNumber',
      resolvedBilling.addressNumber || ''
    );
    mainForm.setFieldValue(
      'billingComplement',
      resolvedBilling.addressComplement || ''
    );
    mainForm.setFieldValue(
      'billingNeighborhood',
      resolvedBilling.neighborhood || ''
    );
    mainForm.setFieldValue('billingCity', resolvedBilling.city || '');
    mainForm.setFieldValue('billingState', resolvedBilling.state || '');

    if (isBusinessProposal) {
      const firstRiskPk = proposal?.lobs?.[0]?.risks?.[0]?.pk;
      if (firstRiskPk && !mainForm.state.values.selectedRiskPk) {
        mainForm.setFieldValue('selectedRiskPk', String(firstRiskPk));
      }
    }

    setIsPrefilled(true);
  }, [isPrefilled, mainForm, proposal, isBusinessProposal]);

  const handleNextStep = () => {
    if (currentStep < (schemaCategory?.steps?.length || 0) - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (isViewMode && stepIndex > currentStep) return;
    setCurrentStep(stepIndex);
  };

  if (policyId && isLoadingProposal && !proposal) {
    return (
      <div className='flex items-center justify-center py-16'>
        <div className='flex items-center gap-3'>
          <Spinner size='sm' colorScheme='gray' />
          <Text className='text-sm text-alba-gray-600'>
            Carregando proposta...
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8 lg:flex-row'>
      <div className='flex-1 space-y-8 pb-6'>
        <div className='md:hidden'>
          <div className='mb-3 text-sm font-medium text-alba-gray-600'>
            Etapa {currentStep + 1} de {schemaCategory?.steps.length || 0}
          </div>
          <div className='overflow-hidden rounded-2xl bg-white px-3 py-5 shadow-md'>
            <div className='grid grid-cols-3 gap-1'>
              {schemaCategory?.steps.map((step, index) => {
                const isActive = index === currentStep;
                const isCompleted = index < currentStep;

                return (
                  <div
                    key={step.step}
                    className='flex min-w-0 flex-col items-center gap-2'
                  >
                    <div
                      className={[
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold',
                        isActive
                          ? 'bg-alba-blue-500 text-white'
                          : isCompleted
                            ? 'bg-alba-green-500 text-white'
                            : 'bg-alba-gray-200 text-alba-gray-600',
                      ].join(' ')}
                    >
                      {index + 1}
                    </div>
                    <span
                      className={[
                        'line-clamp-2 min-w-0 text-center text-[10px] leading-tight',
                        isActive
                          ? 'font-medium text-alba-black-500'
                          : 'text-alba-gray-600',
                      ].join(' ')}
                      title={step.stepName}
                    >
                      {step.stepName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <RequiredTooltipProvider restartKey={currentStep}>
          <Steps.Root
            step={currentStep}
            count={schemaCategory?.steps.length || 0}
            linear={!isViewMode}
            onStepChange={details => handleStepClick(details.step)}
          >
            <div className='hidden md:block'>
              <Steps.List>
                {schemaCategory?.steps.map((step, index) => (
                  <Steps.Item key={step.step} index={index}>
                    <Steps.Trigger disabled={isViewMode || !proposal}>
                      <Steps.Indicator />
                      <Steps.Title>{step.stepName}</Steps.Title>
                    </Steps.Trigger>
                    {index < schemaCategory?.steps.length - 1 && (
                      <Steps.Separator />
                    )}
                  </Steps.Item>
                ))}
              </Steps.List>
            </div>

            <Steps.Content index={0}>
              {currentStep === 0 &&
                (policyId && proposal && !isPrefilled ? (
                  <div className='flex justify-center py-16'>
                    <div className='flex items-center gap-3'>
                      <Spinner size='sm' colorScheme='gray' />
                      <Text className='text-sm text-alba-gray-600'>
                        Carregando dados da proposta...
                      </Text>
                    </div>
                  </div>
                ) : (
                  <StepInsureData
                    // @ts-ignore - Type incompatibility with composite forms
                    form={mainForm}
                    onNext={handleNextStep}
                    onBack={handlePreviousStep}
                    isViewMode={isViewMode}
                    proposalId={policyId || ''}
                    proposal={proposal}
                    isBusinessProposal={isBusinessProposal}
                  />
                ))}
            </Steps.Content>

            <Steps.Content index={1}>
              {currentStep === 1 && proposal && (
                <StepPayment
                  onBack={handlePreviousStep}
                  onNext={handleNextStep}
                  isViewMode={isViewMode}
                  policyId={policyId}
                  proposal={proposal}
                  effectiveDate={mainForm.state.values.startDate}
                  terminateDate={mainForm.state.values.endDate}
                  productType={paymentProductType}
                  isBusinessProposal={isBusinessProposal}
                  allowDifferentPayment={allowDifferentPayment}
                  allowedPaymentMethodCodes={allowedPaymentMethodCodes}
                  gatewayPaymentMethodCodes={gatewayPaymentMethodCodes}
                  hiddenPaymentMethodCodes={hiddenPaymentMethodCodes}
                  chargeLeadDays={chargeLeadDays}
                  pixAutomatic={pixAutomatic}
                  cardSingleTransaction={cardSingleTransaction}
                />
              )}
            </Steps.Content>

            <Steps.Content index={2}>
              {currentStep === 2 && proposal && (
                <StepResume
                  proposal={proposal}
                  isViewMode={isViewMode}
                  productType={paymentProductType}
                  isBusinessProposal={isBusinessProposal}
                  onResumeEdit={
                    isViewMode ? undefined : () => setCurrentStep(0)
                  }
                />
              )}
            </Steps.Content>
          </Steps.Root>
        </RequiredTooltipProvider>
      </div>
    </div>
  );
}
